// 音声で回答できる端末かどうかの判定 (spec: docs/superpowers/specs/2026-08-04-unsupported-device-notice-design.md)。
//
// 発話区間の検出 (Silero VAD) は onnxruntime-web の wasm 上で動く。1.27 が同梱するのは
// ort-wasm-simd-threaded.* だけで**非 SIMD ビルドが存在しない**ため、WebAssembly SIMD が
// 無い環境では MicVAD.new() が原理的に成功しない。SIMD は Safari 16.4 で追加されたが、
// 16.4 の実装にはバグがあり onnxruntime-web の出力が壊れる (microsoft/onnxruntime#15644、
// Safari 16.5 で修正)。16.4 は「wasm は読めるが推論結果が壊れる」= エラーも出ない壊れ方を
// しうるので、機能検出だけでなく WebKit のバージョンでも弾く。
//
// micPermission.ts の checkMicSupport() と対になる位置づけ。あちらが「マイクの API が
// あるか」を見るのに対し、こちらは「発話区間の検出が動くか」を見る。

export type VoiceUnsupportedReason = "no-wasm-simd" | "webkit-too-old";

export type VoiceSupport =
  | { supported: true }
  | { supported: false; reason: VoiceUnsupportedReason };

export type VoiceSupportEnv = {
  userAgent: string;
  wasmSimd: boolean;
};

/** 対応する WebKit の下限 (spec D2)。16.4 は SIMD 実装のバグを踏むため含めない。 */
const MIN_WEBKIT_MAJOR = 16;
const MIN_WEBKIT_MINOR = 5;

// v128 (SIMD) 命令を含む最小の wasm モジュール。wasm-feature-detect の simd() と同じ
// バイト列で、SIMD 非対応の WebKit はこれを検証段階で拒否する。
const SIMD_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15,
  253, 98, 11,
]);

/**
 * WebAssembly SIMD が使えるかを同期で判定する。
 * 判定自体でアプリを止めないよう、例外も未対応環境も false に倒す (原則 5)。
 */
export function hasWasmSimd(): boolean {
  try {
    if (typeof WebAssembly === "undefined") return false;
    return WebAssembly.validate(SIMD_PROBE);
  } catch {
    return false;
  }
}

/**
 * UA から WebKit のバージョンを読む。読めなければ null (判定しない)。
 *
 * 手掛かりは 2 通りある。
 *   iOS / iPadOS          "CPU iPhone OS 16_4 like Mac OS X" / "CPU OS 16_4 like Mac OS X"
 *   iPadOS のデスクトップ表示 / macOS Safari   "Version/16.4 Safari/605.1.15"
 *
 * 後者には除外条件が要る。Chromium 系はどれも UA 末尾に Safari/537.36 を含み、
 * **Android の WebView は Version/4.0 を名乗る**ので、素通しすると 4.0 < 16.5 で
 * Android 端末が軒並み非対応になる。iOS 上の Chrome は CriOS を名乗り Version/ を
 * 持たないため、前者で拾われる。
 */
function readWebKitVersion(userAgent: string): { major: number; minor: number } | null {
  const os = /CPU (?:iPhone )?OS (\d+)_(\d+)/.exec(userAgent);
  if (os) return { major: Number(os[1]), minor: Number(os[2]) };

  if (/Chrom(e|ium)|Android/.test(userAgent)) return null;
  const version = /Version\/(\d+)\.(\d+)\S* Safari/.exec(userAgent);
  if (version) return { major: Number(version[1]), minor: Number(version[2]) };

  return null;
}

/**
 * 音声で回答できる端末かを判定する純関数。
 *
 * 評価順は機能検出 → バージョン。機能検出が主軸 (UA は偽装も未知の形式もありうる) なので
 * 先に置き、両方に該当する端末 (iOS 16.3 以下) は "no-wasm-simd" になる。
 *
 * UA からバージョンを読めない場合は**対応扱いに倒す** (spec D4)。判定の主軸は機能検出で、
 * UA が読めないことを理由に弾くと未知のブラウザを一律で締め出すため。誤って回答させる
 * 代償 (従来と同じ) より、誤って弾く代償 (一切回答できない) の方が大きい。
 */
export function checkVoiceSupport(env: VoiceSupportEnv): VoiceSupport {
  if (!env.wasmSimd) return { supported: false, reason: "no-wasm-simd" };

  const webkit = readWebKitVersion(env.userAgent);
  if (
    webkit &&
    (webkit.major < MIN_WEBKIT_MAJOR ||
      (webkit.major === MIN_WEBKIT_MAJOR && webkit.minor < MIN_WEBKIT_MINOR))
  ) {
    return { supported: false, reason: "webkit-too-old" };
  }

  return { supported: true };
}

/** 実環境 (navigator / WebAssembly) を読んで判定する。環境に触るのはここだけ (原則 4)。 */
export function detectVoiceSupport(): VoiceSupport {
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return checkVoiceSupport({ userAgent, wasmSimd: hasWasmSimd() });
}
